import { CrewAgent } from '../../types/crew-types';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { MessageSquare } from 'lucide-react';

interface AgentCardProps {
  agent: CrewAgent;
  onClick: (agent: CrewAgent) => void;
  isInViewport?: boolean;
}

const AgentCard = ({ agent, onClick, isInViewport = true }: AgentCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  

  // Get base file name to derive video paths
  const getBaseFileName = (path: string) => {
    const fileName = path.split('/').pop()?.split('.')[0];
    return fileName;
  };

  // Construct hello video path
  const getHelloVideoPath = () => {
    const baseName = getBaseFileName(agent.imagePath);

    // Most agents have their own hello videos with the same name as their image
    // Only handle special cases where the video name differs from the image name

    // Special naming cases:
    if (baseName === 'adjacent') return 'https://assets.pagemind.ai/crew-agents/adjacent-topics-hello.mp4';
    if (baseName === 'webanalytics') return 'https://assets.pagemind.ai/crew-agents/webanalytics-hello.mp4';
    if (baseName === 'handsoff') return 'https://assets.pagemind.ai/crew-agents/handsoff-hello.mp4';
    if (baseName === 'narrator') return 'https://assets.pagemind.ai/crew-agents/narrator-hello.mp4';
    if (baseName === 'site-sector-classifier') return 'https://assets.pagemind.ai/crew-agents/site-sector-classifier-hello.mp4';
    if (baseName === 'customer-segmentation-agent') return 'https://assets.pagemind.ai/crew-agents/customer-segmentation-agent-hello.mp4';
    if (baseName === 'segment-research-agent') return 'https://assets.pagemind.ai/crew-agents/segment-research-agent-hello.mp4';
    if (baseName === 'seo-specialist') return 'https://assets.pagemind.ai/crew-agents/seo-specialist-hello.mp4';

    // Special agent ID mappings:
    if (agent.id === 'voicebot') return 'https://assets.pagemind.ai/crew-agents/voicebot-hello.mp4';
    if (agent.id === 'voice-chatbot') return 'https://assets.pagemind.ai/crew-agents/content-generator-hello.mp4';
    if (agent.id === 'page-narrator') return 'https://assets.pagemind.ai/crew-agents/narrator-hello.mp4';
    if (agent.id === 'seo-aeo-specialist') return 'https://assets.pagemind.ai/crew-agents/seo-specialist-hello.mp4';

    // Analytics agents mappings:
    if (agent.id === 'chatbot-intent-classifier') return 'https://assets.pagemind.ai/crew-agents/chatbot-intent-classifier-hello.mp4';
    if (agent.id === 'chatbot-quality-scorer') return 'https://assets.pagemind.ai/crew-agents/chatbot-quality-scorer-hello.mp4';
    if (agent.id === 'chatbot-content-gap-detector') return 'https://assets.pagemind.ai/crew-agents/chatbot-content-gap-detector-hello.mp4';
    if (agent.id === 'chatbot-conversation-flow-analyzer') return 'https://assets.pagemind.ai/crew-agents/chatbot-conversation-flow-analyzer-hello.mp4';
    if (agent.id === 'chatbot-analytics-agent') return 'https://assets.pagemind.ai/crew-agents/chatbot-analytics-agent-hello.mp4';

    // For content-recommender, we don't have a video, use content-generator
    if (baseName === 'content-recommender') return 'https://assets.pagemind.ai/crew-agents/content-generator-hello.mp4';

    // Default: use the standard naming pattern
    return `https://assets.pagemind.ai/crew-agents/${baseName}-hello.mp4`;
  };

  // Preload video when in viewport OR on first hover
  useEffect(() => {
    if (isInViewport && !videoLoaded && !videoError) {
      const videoPath = getHelloVideoPath();
      const video = document.createElement('video');
      
      video.onloadeddata = () => {
        setVideoLoaded(true);
      };
      
      video.onerror = () => {
        setVideoError(true);
      };
      
      // Set video properties for preloading
      video.src = videoPath;
      video.load();
    }
  }, [isInViewport, videoLoaded, videoError, agent.name]);
  
  // Also try to load video on first hover if not already loaded
  useEffect(() => {
    if (isHovered && !videoLoaded && !videoError && !isInViewport) {
      const videoPath = getHelloVideoPath();
      const video = document.createElement('video');
      
      video.onloadeddata = () => {
        setVideoLoaded(true);
      };
      
      video.onerror = () => {
        setVideoError(true);
      };
      
      video.src = videoPath;
      video.load();
    }
  }, [isHovered, videoLoaded, videoError, isInViewport, agent.name]);

  // Play video when hovered
  useEffect(() => {
    const videoElement = videoRef.current;
    if (isHovered && videoElement && videoLoaded && !videoError) {
      // Reset and play video
      videoElement.currentTime = 0;
      const playPromise = videoElement.play();
      
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          // Only log real errors, not abort errors from quick hover/unhover
          if (error.name !== 'AbortError') {
            setVideoError(true);
          }
        });
      }
    } else if (!isHovered && videoElement) {
      videoElement.pause();
    }
  }, [isHovered, videoLoaded, videoError, agent.name]);

  return (
    <div
      ref={cardRef}
      className="agent-card group flex flex-col h-full"
      onClick={(e) => {
        e.stopPropagation();
        onClick(agent);
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="mb-2 relative h-40 w-40 mx-auto flex-shrink-0 overflow-hidden rounded-lg"
           onMouseEnter={() => setIsHovered(true)}
           onMouseLeave={() => setIsHovered(false)}>
        {isHovered && videoLoaded && !videoError ? (
          <video
            ref={videoRef}
            src={getHelloVideoPath()}
            className="agent-media absolute inset-0 object-contain w-full h-full rounded-lg"
            muted
            playsInline
            preload="auto"
          />
        ) : (
          <img
            src={agent.imagePath}
            alt={agent.name}
            className="agent-media absolute inset-0 object-contain w-full h-full rounded-lg"
          />
        )}
        {/* Preload video element hidden */}
        {!videoError && (
          <video
            src={getHelloVideoPath()}
            className="hidden"
            muted
            playsInline
            preload="metadata"
          />
        )}
      </div>
      <h3 className="text-lg font-semibold mb-1 text-center">{agent.name}</h3>
      <p className="text-sm text-muted-foreground text-center mb-3 flex-grow">{agent.shortDescription}</p>

      <div className="flex gap-1 flex-wrap justify-center mb-4">
        {agent.category.map((cat) => (
          <span
            key={cat}
            className="inline-block px-2 py-1 text-xs rounded-full bg-primary/10 text-primary"
          >
            {cat}
          </span>
        ))}
      </div>

      <div className="mt-auto">
        <Button
          size="sm"
          variant="outline"
          className="w-full"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/insights/hub/${agent.id}`);
          }}
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Let's chat
        </Button>
      </div>
    </div>
  );
};

export default AgentCard;